import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  FormControl,
  FormLabel,
  Typography,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  EntityCriteriaOperator,
  IEntitySelectionCriteria,
  IEntityCriteriaCompoundOperator,
} from '../../../../common/types/shared/entitySelection';
import { v4 as uuIdV4 } from 'uuid';
import { QueryableFieldsQuery } from '../../../../types/generated-types';
import {
  formatCompoundOperatorDisplayString,
  formatCriterionDisplayString,
  formatSimpleOperatorDisplayString,
} from '../helpers';

interface CombineCriteriaDialogProps {
  open: boolean;
  onClose: () => void;
  selectedCriteria: IEntitySelectionCriteria;
  targetModel: string;
  fieldsData: QueryableFieldsQuery['queryableFields'];
  onCombine: (operator: IEntityCriteriaCompoundOperator) => void;
}

export const CombineCriteriaDialog: React.FC<CombineCriteriaDialogProps> = ({
  open,
  onClose,
  selectedCriteria,
  targetModel,
  fieldsData,
  onCombine,
}) => {
  const [combiningOperator, setCombiningOperator] =
    React.useState<EntityCriteriaOperator>(EntityCriteriaOperator.AND);

  const handleCombine = () => {
    const newOperator: IEntityCriteriaCompoundOperator = {
      id: uuIdV4(),
      operator: combiningOperator,
      criteria: {
        criteria: selectedCriteria.criteria || [],
        simpleOperators: selectedCriteria.simpleOperators || [],
        compoundOperators: selectedCriteria.compoundOperators || [],
      },
      invert: false,
    };
    onCombine(newOperator);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Combine Criteria</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Combining Operator</FormLabel>
            <RadioGroup
              value={combiningOperator}
              onChange={(e) =>
                setCombiningOperator(e.target.value as EntityCriteriaOperator)
              }
            >
              <FormControlLabel
                value={EntityCriteriaOperator.AND}
                control={<Radio />}
                label="AND"
              />
              <FormControlLabel
                value={EntityCriteriaOperator.OR}
                control={<Radio />}
                label="OR"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Selected Items
          </Typography>
          {selectedCriteria.criteria &&
            selectedCriteria.criteria.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Criteria
                </Typography>
                <List>
                  {selectedCriteria.criteria.map((criterion) => (
                    <ListItem key={criterion.id}>
                      <ListItemText
                        primary={formatCriterionDisplayString(
                          criterion,
                          fieldsData,
                        )}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

          {selectedCriteria.simpleOperators &&
            selectedCriteria.simpleOperators.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Simple Operators
                </Typography>
                <List>
                  {selectedCriteria.simpleOperators.map((o) => (
                    <ListItem key={o.id}>
                      <ListItemText
                        primary={formatSimpleOperatorDisplayString(
                          o,
                          fieldsData,
                        )}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

          {selectedCriteria.compoundOperators &&
            selectedCriteria.compoundOperators.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Compound Operators
                </Typography>
                <List>
                  {selectedCriteria.compoundOperators.map((o) => (
                    <ListItem key={o.id}>
                      <ListItemText
                        primary={formatCompoundOperatorDisplayString(
                          o,
                          targetModel,
                          fieldsData,
                        )}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleCombine}
          variant="contained"
          color="primary"
          disabled={
            !selectedCriteria.criteria?.length &&
            !selectedCriteria.simpleOperators?.length &&
            !selectedCriteria.compoundOperators?.length
          }
        >
          Combine
        </Button>
      </DialogActions>
    </Dialog>
  );
};
